import { ListMenu } from "./menu";
import { renderBoard, type ScoreBoards } from "./screens";

/** The shared board stays separate from this device's replay library. */
export class GlobalLeaderboardScreen {
  readonly menu: ListMenu;
  private readonly boardTitle: HTMLDivElement;
  private readonly table: HTMLTableElement;
  private readonly status: HTMLDivElement;
  private request = 0;
  private courseIndex = 0;
  private loadingIndex: number | null = null;

  constructor(
    parent: HTMLElement,
    private readonly scores: ScoreBoards,
    private readonly courses: { name: string; title: string }[],
    private readonly host: { onBack(): void; onLocal(): void },
  ) {
    this.menu = new ListMenu(parent, {
      className: "global-leaderboard-menu",
      title: "Global leaderboard",
      hint: "choose a course to see the world's best times",
      touchHint: "tap a course for its top ten",
      scrollPanes: true,
      back: "Back",
      onCancel: () => this.host.onBack(),
      onHighlight: (i) => {
        if (i < this.courses.length) void this.load(i);
      },
    });
    const board = document.createElement("div");
    board.className = "board";
    this.boardTitle = document.createElement("div");
    this.boardTitle.className = "board-title";
    this.table = document.createElement("table");
    this.table.className = "board-table";
    this.status = document.createElement("div");
    this.status.className = "global-status";
    this.status.setAttribute("role", "status");
    board.append(this.boardTitle, this.table, this.status);
    this.menu.content.appendChild(board);
    this.menu.content.tabIndex = 0;
    this.menu.content.setAttribute("role", "region");
    this.menu.content.setAttribute("aria-label", "Leaderboard scores");
  }

  open(courseName: string): void {
    const selected = Math.max(
      0,
      this.courses.findIndex((c) => c.name === courseName),
    );
    this.menu.setItems(
      [
        ...this.courses.map((course, i) => ({
          label: course.title,
          action: () => {
            this.menu.select(i);
            void this.load(i);
          },
        })),
        { label: "Refresh", action: () => void this.load(this.courseIndex) },
        { label: "On this device", action: () => this.host.onLocal() },
      ],
      selected,
    );
    this.menu.show(selected);
  }

  private async load(index: number): Promise<void> {
    const course = this.courses[index];
    if (!course || this.loadingIndex === index) return;
    this.loadingIndex = index;
    this.courseIndex = index;
    const request = ++this.request;
    this.menu.content.scrollTop = 0;
    this.boardTitle.textContent = `${course.title} · worldwide top ten`;
    this.table.replaceChildren();
    this.status.textContent = "Loading scores…";
    try {
      // A queued personal best must finish uploading before the board is read, as on the course
      // and finish screens. Otherwise this screen can show the older board after saying it synced.
      await this.scores.flush();
      if (request !== this.request) return;
      const entries = await this.scores.load(course.name);
      if (request !== this.request) return;
      renderBoard(this.table, entries, null, { global: true });
      this.status.textContent =
        this.scores.pendingCount > 0 ? "Your offline scores are waiting to sync." : "";
    } catch {
      if (request !== this.request) return;
      this.status.textContent =
        "Can't reach the global leaderboard. Your local scores are safe. Try Refresh.";
    } finally {
      if (request === this.request) this.loadingIndex = null;
    }
  }
}
